import { CopyId } from "./CopyId";
import { Note } from "./Note";
import { t } from "../i18n/strings";
import { canReject, getShortfallPercent, isLimitPayment, isShortfallWarned, tokensOf } from "../utils/payments";
import { getDateTime } from "../utils/time";
import type { RefundPreparation } from "../api/types";

/** The same threshold admintgfront warns at (REFUND_SHORTFALL_WARNING_PERCENT). */
export const SHORTFALL_WARNING_PERCENT = 20;

interface RefundConfirmProps {
  preparation: RefundPreparation;
  confirming: boolean;
  rejecting: boolean;
  error: string | null;
  onConfirm: () => void;
  onReject: () => void;
  onCancel: () => void;
}

/**
 * The second step of a refund: the recounted numbers and the decision.
 *
 * Three things here are not cosmetic. Every button is disabled for the whole flight of either
 * decision, so a second click cannot start a second refund, or reject what is being refunded.
 * Leaving any other way calls onCancel, which puts the payment back into the queue: preparing
 * took it out, and a prepared refund nobody decided would sit in `refunding` where nobody sees
 * it again. And the reject button is only there for a request the client made and nobody has
 * answered yet - see canReject.
 */
export function RefundConfirm({
  preparation,
  confirming,
  rejecting,
  error,
  onConfirm,
  onReject,
  onCancel,
}: RefundConfirmProps) {
  const payment = preparation.payment;
  const busy = confirming || rejecting;

  return (
    <div className="refund-confirm">
      <h2 className="refund-confirm__title">{t("refunds.confirmTitle", { id: payment.id })}</h2>

      <div className="refund-confirm__numbers">
        <span className="card__client">
          {t("refunds.client")}
          <CopyId value={payment.user_id} />
        </span>
        <span>{t("refunds.stars", { amount: payment.price_amount })}</span>
        {isLimitPayment(payment) ? (
          preparation.limit_after != null ? (
            <span>{t("refunds.limitAfter", { limit: preparation.limit_after })}</span>
          ) : null
        ) : (
          <>
            <span>{t("refunds.tokens", { tokens: tokensOf(payment.tokens) })}</span>
            <span>{t("refunds.clawback", { tokens: tokensOf(preparation.clawback_tokens) })}</span>
            <span>{t("refunds.unrecovered", { tokens: tokensOf(preparation.shortfall) })}</span>
          </>
        )}
        <span>{t("refunds.previousRefunds", { count: preparation.previous_refunds_count })}</span>
      </div>

      {payment.refund_rejected_at ? (
        <Note kind="warning">{t("refunds.rejectedBefore", { date: getDateTime(payment.refund_rejected_at) })}</Note>
      ) : null}

      {isShortfallWarned(preparation, SHORTFALL_WARNING_PERCENT) ? (
        <Note kind="warning">
          {t("refunds.shortfall", { percent: SHORTFALL_WARNING_PERCENT })} ({getShortfallPercent(preparation)}%)
        </Note>
      ) : null}

      {error ? <Note kind="error">{error}</Note> : null}

      <div className="refund-confirm__buttons">
        <button type="button" className="button button--danger" disabled={busy} onClick={onConfirm}>
          {confirming ? t("refunds.working") : t("refunds.confirm")}
        </button>
        {canReject(payment) ? (
          <button type="button" className="button" disabled={busy} onClick={onReject}>
            {rejecting ? t("refunds.rejecting") : t("refunds.reject")}
          </button>
        ) : null}
        <button type="button" className="button button--quiet" disabled={busy} onClick={onCancel}>
          {t("refunds.cancel")}
        </button>
      </div>
    </div>
  );
}
